import React from 'react';
import Link from 'next/link';
import { cn } from '@/lib/twUtils';

const tones = {
  /** Card bianca su fondo grigio */
  paper: 'bg-white border border-edge text-ink',
  /** Card grigia su fondo bianco */
  mist: 'bg-mist border border-edge-soft text-ink',
  /** Card navy piena, per il blocco in evidenza */
  navy: 'bg-primary text-white',
  /** Vetro su banda navy */
  glass: 'bg-white/6 border border-edge-on-dark text-white',
  /** Gradiente chiaro, per il blocco riepilogo */
  gradient: 'bg-[linear-gradient(150deg,#ffffff,#e6edf5)] border border-edge text-ink',
  /** Arancione pieno, per una sola testimonianza per griglia */
  accent: 'bg-accent text-white',
} as const;

const paddings = {
  lg: 'p-[clamp(1.5rem,3vw,2.375rem)]',
  md: 'p-[clamp(1.5rem,3vw,2rem)]',
  sm: 'p-[1.625rem]',
} as const;

const radii = {
  lg: 'rounded-ds-lg',
  xl: 'rounded-ds-xl',
} as const;

const hoverByTone: Record<keyof typeof tones, string> = {
  paper: 'hover:-translate-y-[3px] hover:border-edge-strong',
  mist: 'hover:-translate-y-[3px] hover:border-edge-strong',
  navy: 'hover:-translate-y-[3px]',
  glass: 'hover:-translate-y-[3px] hover:bg-white/12',
  gradient: 'hover:-translate-y-[3px]',
  accent: 'hover:-translate-y-[3px]',
};

export interface CardProps {
  tone?: keyof typeof tones;
  padding?: keyof typeof paddings;
  radius?: keyof typeof radii;
  /** Rende tutta la card cliccabile e attiva il sollevamento all'hover */
  href?: string;
  className?: string;
  children?: React.ReactNode;
  id?: string;
}

/** Superficie del design system: un solo posto per fondi, bordi e raggi. */
const Card = ({
  tone = 'paper',
  padding = 'md',
  radius = 'lg',
  href,
  className,
  children,
  id,
}: CardProps) => {
  const classes = cn(
    'relative flex flex-col',
    tones[tone],
    paddings[padding],
    radii[radius],
    href &&
      cn(
        'ds-focus transition-[transform,background-color,border-color] duration-200',
        'motion-reduce:transition-none motion-reduce:hover:translate-y-0',
        hoverByTone[tone]
      ),
    className
  );

  if (!href) {
    return (
      <div id={id} className={classes}>
        {children}
      </div>
    );
  }

  return (
    <Link id={id} href={href} className={classes}>
      {children}
    </Link>
  );
};

export default Card;
